
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
import { useEffect, useState } from 'react';
import { GetServerSidePropsContext, GetServerSidePropsResult, NextPage } from 'next';
import { useAuth } from 'context/authContext';
import useTranslation from 'next-translate/useTranslation';
import { Carousel } from 'components/Carousel';
import CategorySection from 'components/CategorySections/CategorySection';
import Layout from 'components/Layout';
import { Namespace, PlaylistType } from 'enums';
import styles from './index.module.scss';
import Head from 'next/head';
import { basicSEO } from 'helpers/jsx';
import { Permalink } from 'helpers';
import { StaticPathParams } from 'typings/staticPaths';
import { authorizeUserServerSide } from 'api/firebaseAdmin';
import { useLang } from 'context/langContext';
import { HomepageSection } from 'typings/api/homepage';
import { FeaturedSectionType } from 'enums/FeaturedSectionType';
import { BrowseSkeleton } from 'components';

interface Props {
  carouselSection: HomepageSection;
  sections: HomepageSection[];
  countryCode: string;
}

const Browse: NextPage<Props> = ({ carouselSection, sections, countryCode }) => {
  const { t } = useTranslation(Namespace.Common);
  const { reloadUser } = useAuth();
  const { locale } = useLang();

  const [showSkeleton, setShowSkeleton] = useState(true);

  useEffect(() => {
    reloadUser();
  }, []);

  const linkToOriginals = Permalink.dramoxOriginals(locale);

  // wait for the first carousel image to load, then show everything
  useEffect(() => {
    if (!carouselSection) {
      setShowSkeleton(false);
    } else if (carouselSection?.plays?.length) {
      // eslint-disable-next-line prefer-const
      let loadTimeout: number;

      const onLoad = () => {
        setShowSkeleton(false);
        clearTimeout(loadTimeout);
      };
      const img = new Image();
      img.onload = onLoad;
      img.onerror = onLoad;
      img.src = carouselSection.plays[0].poster_url;

      loadTimeout = window.setTimeout(onLoad, 3000);
    }
  }, [carouselSection]);

  if (showSkeleton) {
    return <BrowseSkeleton countryCode={countryCode} />;
  }

  return (
    <Layout countryCode={countryCode}>
      <Head>{basicSEO(t('seo:browse_title'), t('seo:browse_description'))}</Head>
      <main className={styles.homepage}>
        <Carousel section={carouselSection} linkToOriginals={linkToOriginals} />
        {sections.map((s) => {
          let link: string;
          let title = s.name;

          switch (s.type) {
            case FeaturedSectionType.POPULAR:
              title = t('common:most_popular');
              link = Permalink.mostPopular(locale);
              break;
            case FeaturedSectionType.WATCHING:
              title = t('common:currently_watching');
              link = Permalink.currentlyWatching(locale);
              break;
            case FeaturedSectionType.NEWEST:
              title = t('common:recently_added');
              link = Permalink.recentlyAdded(locale);
              break;
            case FeaturedSectionType.ORIGINALS:
              link = linkToOriginals;
              break;
            case FeaturedSectionType.WATCHLIST:
              title = t('common:watch_list');
              link = Permalink.watchList(locale);
              break;
          }

          if (!link) {
            link = s.genre ? Permalink.genre(s.genre, locale) : Permalink.featuredSection(s, locale);
          }

          return (
            <CategorySection
              playlistType={PlaylistType.Section}
              title={title}
              plays={s.plays}
              moreHref={link}
              key={s.id}
              showResume={s.type === FeaturedSectionType.WATCHING}
              mediumThumbs={s.type === FeaturedSectionType.CUSTOM_MEDIUM}
              largeThumbs={s.type === FeaturedSectionType.ORIGINALS}
            />
          );
        })}
      </main>
    </Layout>
  );
};

 async function _getServerSideProps(
  ctx: GetServerSidePropsContext<StaticPathParams>,
): Promise<GetServerSidePropsResult<Props>> {
  const { user, locale, apiClient, countryCode } = await authorizeUserServerSide(ctx);

  if (user && user.isWeekOldWithoutSub) {
    return {
      redirect: {
        permanent: false,
        destination: Permalink.choosePlan(),
      },
    };
  }

  const { sections: _sections } = await apiClient.getHomepageSections(user, 10);
  const carouselSection = _sections.find((s) => s.type === FeaturedSectionType.BANNER) ?? null;
  const sections = _sections.filter((s) => s.type !== carouselSection?.type);

  return {
    props: {
      carouselSection,
      sections,
      countryCode,
    },
  };
}

export default Browse;


// @ts-ignore
    export async function getServerSideProps(ctx) {
// @ts-ignore
        let res = _getServerSideProps(ctx)
// @ts-ignore
        if(typeof res.then === 'function') res = await res
// @ts-ignore
        return {
// @ts-ignore
          
// @ts-ignore
          ...res,
// @ts-ignore
          props: {
// @ts-ignore
            ...(res.props || {}),
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/browse/index',
// @ts-ignore
              loaderName: 'getServerSideProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  